import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { EditorialSubsection } from '../../report/editorial';

/**
 * RTU-1 — Environmental modern decomposition.
 * Ecological assessment covering ESA §7 and CWA §404 dimensions.
 */
export const EcologicalAssessmentSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const data = report.ecologicalAssessment;
  const hasData = !!data && !!(
    data.criticalHabitatAnalysis ||
    data.esaSpeciesAssessment ||
    data.wetlandsDelineation ||
    data.wildlifeCorridors ||
    data.section7Requirements ||
    data.section404Requirements
  );

  const primaryFields: Array<{ key: keyof NonNullable<typeof data>; label: string }> = [
    { key: 'criticalHabitatAnalysis', label: 'Critical Habitat (ESA)' },
    { key: 'esaSpeciesAssessment', label: 'ESA-Listed Species' },
    { key: 'wetlandsDelineation', label: 'Wetlands Delineation (CWA 404)' },
    { key: 'wildlifeCorridors', label: 'Wildlife Corridors' },
  ];

  // The §7 / §404 requirement blocks formerly carried a success / primary
  // accent wash; under the editorial language the CWA §404 block uses the
  // accent label ink to preserve that visual distinction (theme-aware).
  const requirementFields: Array<{ key: keyof NonNullable<typeof data>; label: string; accentLabel?: boolean }> = [
    { key: 'section7Requirements', label: 'ESA Section 7 Requirements' },
    { key: 'section404Requirements', label: 'CWA Section 404 Requirements', accentLabel: true },
  ];

  return (
    <SectionShell label={label} hasData={hasData}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {primaryFields.map(({ key, label: fieldLabel }) => {
          const value = data?.[key];
          if (!value || typeof value !== 'string') return null;
          return (
            <EditorialSubsection key={key} theme={theme} label={fieldLabel}>
              {value}
            </EditorialSubsection>
          );
        })}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {requirementFields.map(({ key, label: fieldLabel, accentLabel }) => {
          const value = data?.[key];
          if (!value || typeof value !== 'string') return null;
          return (
            <EditorialSubsection key={key} theme={theme} label={fieldLabel} accentLabel={accentLabel}>
              {value}
            </EditorialSubsection>
          );
        })}
      </div>
    </SectionShell>
  );
};
